import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import en from "./en/translation.json";
import cs from "./cs/translation.json";

const resources = { en: { translation: en }, cs: { translation: cs } };

export const SUPPORTED_LANGS = Object.keys(resources);
export const FALLBACK_LANG = "en";

const COUNTRY_STORAGE_KEY = "detectedCountry";

i18n.use(initReactI18next).init({
  resources,
  lng: FALLBACK_LANG,
  fallbackLng: FALLBACK_LANG,
  interpolation: { escapeValue: false },
});

export function isCzechRegion(countryCode?: string | null): boolean {
  const country = countryCode?.toLowerCase();
  return country === "cz" || country === "sk";
}

export function getDetectedCountry(): string | null {
  return localStorage.getItem(COUNTRY_STORAGE_KEY);
}

let countryDetectionPromise: Promise<string | null> | null = null;

export async function detectCountry(): Promise<string | null> {
  const cached = getDetectedCountry();
  if (cached) return cached;

  if (!countryDetectionPromise) {
    countryDetectionPromise = (async () => {
      try {
        const res = await fetch("https://ipapi.co/json/");
        const data = await res.json();
        const country = data.country_code?.toLowerCase() || null;
        if (country) {
          localStorage.setItem(COUNTRY_STORAGE_KEY, country);
        }
        return country;
      } catch (err) {
        console.warn("[i18n] Country detection failed:", err);
        return null;
      } finally {
        countryDetectionPromise = null;
      }
    })();
  }

  return countryDetectionPromise;
}

export function setLanguage(lang: string) {
  if (!SUPPORTED_LANGS.includes(lang)) return;
  i18n.changeLanguage(lang);
  localStorage.setItem("lang", lang);
}

export async function detectAndSetLanguage(multilanguage: boolean) {
  try {
    const forceEnglish = localStorage.getItem("forceEnglish") === "1";
    if (forceEnglish) {
      i18n.changeLanguage("en");
      return "en";
    }

    if (!multilanguage) {
      i18n.changeLanguage("en");
      return "en";
    }

    const savedLang = localStorage.getItem("lang");
    if (savedLang && SUPPORTED_LANGS.includes(savedLang)) {
      i18n.changeLanguage(savedLang);
      return savedLang;
    }

    const country = await detectCountry();
    const detected = isCzechRegion(country) ? "cs" : FALLBACK_LANG;

    i18n.changeLanguage(detected);
    localStorage.setItem("lang", detected);
    console.debug(`[i18n] Detected language by IP: ${detected}`);

    return detected;
  } catch (err) {
    console.warn("[i18n] Language detection failed:", err);
    i18n.changeLanguage(FALLBACK_LANG);
    return FALLBACK_LANG;
  }
}

export default i18n;
